<template>
    <!-- 手机CARD部 -->
    <div class="md:hidden" v-loading="loading" style="height: calc(100vh - 242px)">
        <div style="overflow-y: auto; height: calc(100vh - 242px)">
            <div class="flex flex-wrap gap-4 p-4">
                <el-card style="width: 100%; border: 2px solid #d1d5db; border-radius: 10px"
                    v-for="item in data" :key="item.inventory_id">
                    <div class="flex justify-between">
                        <div>{{ item.organization_name }}</div>
                        <span class="material-symbols-outlined cursor-pointer"
                            @click="handleSetConstantButton(item)">
                            settings
                        </span>
                    </div>

                    <div class="flex flex-col justify-between">
                        <div>{{ item.material_name }}</div>
                        <div>{{ item.category_name }}</div>
                    </div>

                    <el-row align="middle">
                        <el-col :span="10" justify="center">
                            <div>在庫合計</div>
                            <div class="text-5xl">{{ item.quantity }}</div>
                            <div class="flex justify-center">
                                <span v-if="!item.isExpand" class="material-symbols-outlined cursor-pointer" @click="handleExpandButton(item)">
                                    <el-icon :size="16">
                                        <ArrowDown />
                                    </el-icon>
                                </span>
                                <span v-else class="material-symbols-outlined cursor-pointer" @click="handleCollapseButton(item)">
                                    <el-icon :size="16">
                                        <ArrowUp />
                                    </el-icon>
                                </span>
                            </div>
                        </el-col>

                        <el-col :span="14">
                            <div class="h-[120px]" align="center">
                                <ImgLoading v-if="item.images" v-model="item.images" class="carousel-image" />
                                <div v-else class="w-full h-full bg-gray-100"></div>
                            </div>
                        </el-col>
                    </el-row>
                        
                    <div v-if="item.isExpand" class="mt-2">
                        <div v-if="item.details && item.details.length > 0">
                            <!-- 使用DepartmentLevelInventoryList组件显示部门级别数据 -->
                            <DepartmentLevelInventoryList
                                :data="item.details"
                                :loading="false"
                                :login-user-role="loginUserRole"
                                :login-user-role-is-head="loginUserRoleIsHead"
                                :search-department-id="undefined"
                                :is-standalone="false"
                                @edit="$emit('edit', $event)"
                                @delete="$emit('delete', $event)"
                                @alarm="$emit('alarm', $event)"
                                @constant="$emit('constant', $event)"
                                @expand="$emit('nestedExpand', $event)"
                                @collapse="$emit('collapse', $event)"
                                @quantity-update="$emit('quantityUpdate', $event)"
                                @transfer-inventory="$emit('transferInventory', $event)"
                            />
                        </div>
                        <div v-else class="px-8 py-4 text-gray-500">
                            データを読み込み中...
                        </div>
                    </div>
                </el-card>
            </div>
        </div>
    </div>

    <!-- 表格模式 -->
    <div class="hidden md:block w-full">
        <el-table 
            ref="tableRef"
            :data="data" 
            height="calc(100vh - 260px)" 
            v-loading="loading" 
            row-key="inventory_id"
            @expand-change="handleExpandChange">
            <!-- 展开列 -->
            <el-table-column type="expand" label="詳細" width="80" align="center">
                <template #default="props">
                    <div v-if="props.row.details && props.row.details.length > 0" class="px-8 py-0">
                        <!-- 使用DepartmentLevelInventoryList组件显示部门级别数据 -->
                        <DepartmentLevelInventoryList
                            :data="props.row.details"
                            :loading="false"
                            :login-user-role="loginUserRole"
                            :login-user-role-is-head="loginUserRoleIsHead"
                            :search-department-id="undefined"
                            :is-standalone="false"
                            @edit="$emit('edit', $event)"
                            @delete="$emit('delete', $event)"
                            @alarm="$emit('alarm', $event)"
                            @constant="$emit('constant', $event)"
                            @expand="$emit('nestedExpand', $event)"
                            @collapse="$emit('collapse', $event)"
                            @quantity-update="$emit('quantityUpdate', $event)"
                            @transfer-inventory="$emit('transferInventory', $event)"
                        />
                    </div>
                    <div v-else class="px-8 py-4 text-gray-500">
                        データを読み込み中...
                    </div>
                </template>
            </el-table-column>

            <!-- 主要列 -->
            <el-table-column prop="organization_name" label="所属組織" width="160">
                <template #header>
                    <div class="flex items-center cursor-pointer whitespace-nowrap" @click="handleSort('organization_name')">
                        <span>{{ getSortLabel('organization_name') }}</span>
                        <el-icon class="ml-1 flex-shrink-0">
                            <span class="material-symbols-outlined text-sm" :class="{ 'text-blue-500': sortField === 'organization_name' }">
                                {{ getSortIcon('organization_name') }}
                            </span>
                        </el-icon>
                    </div>
                </template>
            </el-table-column>
            <el-table-column prop="category_name" label="カテゴリ" width="160">
                <template #header>
                    <div class="flex items-center cursor-pointer whitespace-nowrap" @click="handleSort('category_name')">
                        <span>{{ getSortLabel('category_name') }}</span>
                        <el-icon class="ml-1 flex-shrink-0">
                            <span class="material-symbols-outlined text-sm" :class="{ 'text-blue-500': sortField === 'category_name' }">
                                {{ getSortIcon('category_name') }}
                            </span>
                        </el-icon>
                    </div>
                </template>
            </el-table-column>
            <el-table-column prop="material_name" label="アイテム名" min-width="300">
                <template #header>
                    <div class="flex items-center cursor-pointer whitespace-nowrap" @click="handleSort('material_name')">
                        <span>{{ getSortLabel('material_name') }}</span>
                        <el-icon class="ml-1 flex-shrink-0">
                            <span class="material-symbols-outlined text-sm" :class="{ 'text-blue-500': sortField === 'material_name' }">
                                {{ getSortIcon('material_name') }}
                            </span>
                        </el-icon>
                    </div>
                </template>
                <template #default="scope">
                    <div>
                        {{ scope.row.material_name }}<br />
                        <span v-if="scope.row.jancode" class="text-sm text-gray-500">
                            （{{ scope.row.jancode }}）
                        </span>
                    </div>
                </template>
            </el-table-column>

            <el-table-column prop="quantity" label="在庫数" width="100" header-align="center" align="right">
                <template #header>
                    <div class="flex items-center cursor-pointer whitespace-nowrap" @click="handleSort('quantity')">
                        <span>{{ getSortLabel('quantity') }}</span>
                        <el-icon class="ml-1 flex-shrink-0">
                            <span class="material-symbols-outlined text-sm" :class="{ 'text-blue-500': sortField === 'quantity' }">
                                {{ getSortIcon('quantity') }}
                            </span>
                        </el-icon>
                    </div>
                </template>
                <template #default="scope">
                    <span>{{ scope.row.quantity }}</span>
                    <span v-if="scope.row.material_unit" class="text-sm text-gray-500 ml-1">{{ scope.row.material_unit }}</span>
                </template>
            </el-table-column>

            <el-table-column label="操作" width="100" header-align="center" align="center">
                <template #default="scope">
                    <el-button
                        type="info"
                        style="background-color: #b7b7b7;"
                        text
                        bg
                        @click="handleSetConstantButton(scope.row)">
                        <span>定数設定</span>
                    </el-button>
                </template>
            </el-table-column>

            <el-table-column label="画像" width="52" align="center">
                <template #default="scope">
                    <ImgLoading v-if="scope.row.images" v-model="scope.row.images" class="thumbnail" />
                </template>
            </el-table-column>
        </el-table>


    </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, nextTick, PropType } from 'vue';
import { ArrowDown, ArrowUp } from '@element-plus/icons-vue';
import moment from 'moment';
import { ROLES } from '@/config/constants';
import ImgLoading from '@/components/common/ImgLoading.vue';
import DepartmentLevelInventoryList from './DepartmentLevelInventoryList.vue';
import { AlarmInfo, ConstantInfo, InventoryInfo } from './type';

export default defineComponent({
    name: 'OrganizationLevelInventoryList',
    components: {
        ArrowDown,
        ArrowUp,
        ImgLoading,
        DepartmentLevelInventoryList,
    },
    props: {
        data: {
            type: Array as PropType<InventoryInfo[]>,
            required: true,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        loginUserRole: {
            type: String,
            required: true,
        },
        loginUserRoleIsHead: {
            type: Boolean,
            required: true,
        },
        currentPage: {
            type: Number,
            default: 1,
        },
        pageSize: {
            type: Number,
            default: 10,
        },
        totalRecords: {
            type: Number,
            default: 0,
        },
    },
    emits: ['expand', 'nestedExpand', 'collapse', 'edit', 'delete', 'alarm', 'quantityUpdate', 'constant', 'sort', 'size-change', 'current-change', 'transferInventory'],
    setup(props, { emit }) {
        const tableRef = ref();

        // 排序相关状态
        const sortField = ref<string>('material_name'); // 当前排序字段
        const sortOrder = ref<'asc' | 'desc'>('asc'); // 当前排序方向

        // 排序配置
        const sortConfig = {
            'material_name': {
                label: 'アイテム名',
                type: 'string' as const,
                defaultOrder: 'asc' as const
            },
            'quantity': {
                label: '在庫数',
                type: 'number' as const,
                defaultOrder: 'desc' as const
            },
            'organization_name': {
                label: '組織',
                type: 'string' as const,
                defaultOrder: 'asc' as const
            },
            'category_name': {
                label: 'カテゴリ',
                type: 'string' as const,
                defaultOrder: 'asc' as const
            },
            'storage_date': {
                label: '保管日',
                type: 'date' as const,
                defaultOrder: 'desc' as const
            },
            'expiration_date': {
                label: '有効期限',
                type: 'date' as const,
                defaultOrder: 'asc' as const
            }
        };

        // 监听数据变化，重置展开状态
        watch(() => props.data, () => {
            // 当数据变化时，重置表格的展开状态
            if (tableRef.value) {
                // 使用nextTick确保DOM更新完成后再重置
                nextTick(() => {
                    // 清空所有展开的行
                    const expandedRows = tableRef.value.store.states.expandRows;
                    if (expandedRows && expandedRows.length > 0) {
                        expandedRows.forEach((row: any) => {
                            tableRef.value.toggleRowExpansion(row, false);
                        });
                    }
                });
            }
        }, { deep: true });

        const formatDate = (date: string) => {
            return moment(date).format('YYYY/MM/DD');
        };

        const isExpired = (date: string) => {
            return moment(date).isBefore(moment(), 'day');
        };

        const getImgUrls = (images: string) => {
            return images ? images.split(',') : [];
        };

        const handleExpandChange = (row: any, expanded: boolean) => {
            if (expanded) {
                // 展开时触发获取数据
                emit('expand', row);
            } else {
                // 收起时触发收起事件
                emit('collapse', row);
            }
        };

        const handleExpandButton = (row: InventoryInfo) => {
            row.isExpand = true;
            handleExpandChange(row, true);
        };

        const handleCollapseButton = (row: InventoryInfo) => {
            row.isExpand = false;
            handleExpandChange(row, false);
        };

        const handleSetConstantButton = (row: any) => {
            emit('constant', row);
        };

        // 处理排序
        const handleSort = (field: string) => {
            if (sortField.value === field) {
                // 如果点击的是当前排序字段，切换排序方向
                sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
            } else {
                // 如果点击的是新字段，使用该字段的默认排序方向
                sortField.value = field;
                sortOrder.value = sortConfig[field as keyof typeof sortConfig]?.defaultOrder || 'asc';
            }
            // 触发排序事件，让父组件处理
            emit('sort', { field: sortField.value, order: sortOrder.value });
        };

        // 获取排序图标
        const getSortIcon = (field: string) => {
            if (sortField.value !== field) {
                return 'unfold_more'; // 未排序状态
            }
            return sortOrder.value === 'asc' ? 'keyboard_arrow_up' : 'keyboard_arrow_down';
        };

        // 获取排序字段的标签
        const getSortLabel = (field: string) => {
            const config = sortConfig[field as keyof typeof sortConfig];
            return config ? config.label : field;
        };

        // 分页处理方法
        const handleSizeChange = (size: number) => {
            emit('size-change', size);
        };

        const handleCurrentChange = (page: number) => {
            emit('current-change', page);
        };

        return {
            tableRef,
            formatDate,
            isExpired,
            getImgUrls,
            handleExpandChange,
            handleExpandButton,
            handleCollapseButton,
            handleSetConstantButton,
            handleSort,
            getSortIcon,
            getSortLabel,
            handleSizeChange,
            handleCurrentChange,
            sortField,
            sortOrder,
            ROLES,
        };
    },
});
</script>

<style scoped>
.thumbnail {
    width: 30px;
    height: 30px;
    object-fit: cover;
}

.carousel-image {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.custom-title {
    background-color: #f5f5f5;
    padding: 0 16px;
    border-bottom: 1px solid #ddd;
    font-weight: bold;
}

.cursor-pointer {
    cursor: pointer;
}

.hover\:shadow-lg:hover {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

.hover\:text-blue-600:hover {
    color: #2563eb;
}

/* 移除展开内容的所有间距 */
:deep(.el-table__expand-content) {
    margin: 0;
    padding: 0;
}

:deep(.el-table__expand-content .cell) {
    padding: 0;
}

:deep(.el-table__expand-column) {
    padding: 0;
}

:deep(.el-table__expand-column .cell) {
    padding: 0;
}

/* 覆盖展开单元格的默认内边距 */
/* :deep(.el-table__expanded-cell) {
    padding: 0 !important;
} */

/* :deep(.el-table__expanded-cell .cell) {
    padding: 0 !important;
} */

:deep(.el-table__cell.el-table__expanded-cell) {
    padding: 0 !important;
}

/* 排序表头样式 */
.sort-header {
    cursor: pointer;
    user-select: none;
    transition: background-color 0.2s;
    white-space: nowrap;
    display: flex;
    align-items: center;
}

.sort-header:hover {
    background-color: #f5f7fa;
}

/* 确保表头内容不换行 */
:deep(.el-table .el-table__header-wrapper .el-table__header th) {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
</style> 